import {
  getActivityHitPath,
  getGlobalHitPath,
  type ActivityHit,
  type FacilityHit,
  type PersonHit
} from "@/components/AlgoliaSearch";
import { otherFacilityCountFromEmployments } from "@/lib/personSecondaryLine";
import type {
  GlobalSearchActivityNode,
  GlobalSearchFacilityNode,
  GlobalSearchJobNode,
  GlobalSearchPersonNode
} from "./queries";

export type JobHit = {
  objectID: string;
  object_type: "Job";
  name: string;
  facility_id?: string;
  facility_name?: string;
  section_id?: string;
  section_name?: string;
  consultant_name?: string;
  stage?: string;
  status?: string;
};

export function formatJobSearchSupportingText(parts: {
  facilityName?: string | null;
  consultantName?: string | null;
  stage?: string | null;
  status?: string | null;
}): string | undefined {
  const line = [parts.facilityName, parts.consultantName, parts.stage, parts.status]
    .map((value) => value?.trim())
    .filter(Boolean)
    .join(" · ");
  return line || undefined;
}

export type GlobalSearchEntityHit = PersonHit | FacilityHit | JobHit | ActivityHit;

export function isJobHit(hit: GlobalSearchEntityHit): hit is JobHit {
  return hit.object_type === "Job";
}

export function isActivityHit(hit: GlobalSearchEntityHit): hit is ActivityHit {
  return hit.object_type === "Activity";
}

export function isPersonHit(hit: GlobalSearchEntityHit): hit is PersonHit {
  return hit.object_type === "Person";
}

export function getJobHitPath(hit: JobHit): string | null {
  return hit.objectID ? `/jobs/job-post/${hit.objectID}/details` : null;
}

export function getGlobalSearchHitPath(hit: GlobalSearchEntityHit): string | null {
  if (isJobHit(hit)) return getJobHitPath(hit);
  if (isActivityHit(hit)) return getActivityHitPath(hit);
  return getGlobalHitPath(hit);
}

export function toGlobalSearchPersonHit(node: GlobalSearchPersonNode): PersonHit {
  const pgaId = node.pgaId ?? "";
  return {
    objectID: node.id,
    object_type: "Person",
    pga_id: pgaId,
    name: node.displayName,
    preferred_name: node.displayName,
    profile_photo: node.profilePhoto ?? undefined,
    section_id: node.primarySection?.id ?? undefined,
    section_name: node.primarySection?.name ?? undefined,
    facility_name: node.primaryFacility?.name ?? undefined,
    other_facility_count: otherFacilityCountFromEmployments(node.primaryFacility?.id, node.employments),
    state: node.primaryFacility?.addresses?.find((address) => address.state)?.state ?? undefined,
    legacy_id: pgaId
  };
}

export function toGlobalSearchFacilityHit(node: GlobalSearchFacilityNode): FacilityHit {
  return {
    objectID: node.id,
    object_type: "Facility",
    facility_id: node.id,
    name: node.name,
    facility_name: node.name,
    city: node.mainAddress?.city ?? "",
    state: node.mainAddress?.state ?? "",
    section_id: node.section?.id ?? "",
    section_name: node.section?.name ?? "",
    legacy_id: node.legacyId ?? undefined
  };
}

export function toGlobalSearchJobHit(node: GlobalSearchJobNode): JobHit {
  return {
    objectID: node.id,
    object_type: "Job",
    name: node.name,
    facility_id: node.facility?.id ?? undefined,
    facility_name: node.facility?.name ?? undefined,
    section_id: node.facility?.section?.id ?? undefined,
    section_name: node.facility?.section?.name ?? undefined,
    consultant_name: node.owner?.name ?? undefined,
    stage: node.stage ?? undefined,
    status: node.status ?? undefined
  };
}

export function toGlobalSearchActivityHit(node: GlobalSearchActivityNode): ActivityHit {
  const eventSlug = node.event?.slug?.trim() || null;
  return {
    objectID: node.id,
    object_type: "Activity",
    title: node.title,
    internal_description: node.internalDescription,
    activity_type: node.activityType?.title ?? null,
    activity_type_id: node.activityType?.id ?? null,
    managing_section_id: node.managingSection?.id ?? null,
    managing_section_name: node.managingSection?.name ?? null,
    allow_participation_starting: node.allowParticipationStarting,
    allow_participation_through: node.allowParticipationThrough,
    has_event: Boolean(eventSlug),
    event_slug: eventSlug,
    event_title: node.event?.title ?? null,
    event_start_date: node.event?.startsAt ?? null,
    event_end_date: node.event?.endsAt ?? null
  };
}

export function navigableGlobalSearchHits<T extends GlobalSearchEntityHit>(hits: T[]): T[] {
  return hits.filter((hit) => getGlobalSearchHitPath(hit));
}
